/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import TableHead from '@mui/material/TableHead';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDDateRange from 'components/MDDateRange';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  order_by: 'tb.code',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  period: 'ALL',
  branch: 'ALL',
  status: 'ALL',
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

const initSummary = {
  total: '',
  discount: '',
  amount: '',
  vat: '',
  amount_vat: '',
  shipping: '',
  nettotal: '',
  deposit: '',
  balance: '',
};

function RpPurchaseData() {
  const mod = 'rp_purchase_data';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [cboBranch, setCboBranch] = useState([]);
  const [cboStatus, setCboStatus] = useState([]);
  const [summary, setSummary] = useState(initSummary);
  const [urlExcel, setUrlExcel] = useState('');

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const view = (data) => {
    setFind(data);
    setLoading(true);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      branch: data.branch,
      period: data.period,
      status: data.status,
      date_start: data.date_start,
      date_stop: data.date_stop,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);

        setSummary(result.summary);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    let param = `download`;
    param += `?search=${find.search}`;
    param += `&branch=${find.branch}`;
    param += `&status=${find.status}`;
    param += `&period=${find.period}`;
    param += find.period === 'ALL' ? '' : `&date_start=${find.date_start}`;
    param += find.period === 'ALL' ? '' : `&date_stop=${find.date_stop}`;

    window.open(`${urlExcel}/${param}`, '_self');
    // window.open(`${urlExcel}/${param}`, "_blank");
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      page: 1,
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboBranch(result.branch);
        setUrlExcel(result.excel);
        setCboStatus(result.stepper);
        view({ ...find, branch: result.branch_id });
      } else {
        Func.exitApp();
      }
    });
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.branch')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.status}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, status: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>สถานะ :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboStatus.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>

          {find.period === 'ALL' ? (
            <Grid item xs={12} sm={12} md={6} lg={2}>
              <MDButton
                variant='outlined'
                color='secondary'
                fullWidth
                sx={{ p: 1.5 }}
                onClick={() => view({ ...find, period: 'DATE', page: 1 })}
              >
                <Icon>edit_calendar</Icon>&nbsp;เลือกช่วงเวลา
              </MDButton>
            </Grid>
          ) : null}
          {find.period === 'ALL' ? null : (
            <Grid item xs={12} sm={12} md={12} lg={6}>
              <MDDateRange
                dateStart={find.date_start}
                dateStop={find.date_stop}
                callBack={setDateRange}
                closeDate={() => view({ ...find, period: 'ALL', page: 1 })}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={12} md={12} lg={1}>
            <MDButton variant='gradient' color='success' fullWidth onClick={excel}>
              <Icon>table_view</Icon>&nbsp;Excel
            </MDButton>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    const diffDisplay = (row) => {
      if (row.diff_due * 1 > 0) {
        return (
          <MDBox className='nowrap'>
            <code>{row.diff_due} วัน</code>
          </MDBox>
        );
      }

      if (row.diff_due * 1 === 0) {
        return (
          <MDBox className='nowrap'>
            <code className='color-success'>ถึงกำหนด</code>
          </MDBox>
        );
      }

      return null;
    };

    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_purchase'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHead>
                  <TableRow>
                    <TableCell align='center' width={100}>
                      เลขที่บิล
                    </TableCell>
                    <TableCell>ผู้จัดจำหน่าย</TableCell>
                    <TableCell>สาขารับเข้า</TableCell>
                    <TableCell className='nowrap'>หมายเหตุ</TableCell>
                    <TableCell className='nowrap'>ประเภท</TableCell>
                    <TableCell className='nowrap'>ภาษี</TableCell>
                    <TableCell align='center' className='nowrap'>
                      วันสั่งซื้อ
                    </TableCell>
                    <TableCell align='center' className='nowrap'>
                      กำหนดรับ
                    </TableCell>
                    <TableCell align='right' className='nowrap highlight-primary'>
                      มูลค่าสินค้า
                    </TableCell>
                    <TableCell align='right' className='nowrap'>
                      หักส่วนลด
                    </TableCell>
                    <TableCell align='right' className='nowrap'>
                      หักส่วนลด
                    </TableCell>
                    <TableCell align='right' className='nowrap highlight-danger'>
                      ส่วนลด
                    </TableCell>
                    <TableCell align='right' className='nowrap'>
                      หลังหักส่วนลด
                    </TableCell>
                    <TableCell align='right' className='nowrap'>
                      Vat
                    </TableCell>
                    <TableCell align='right' className='nowrap'>
                      ภาษีมูลค่าเพิ่ม
                    </TableCell>
                    <TableCell align='right' className='nowrap'>
                      มูลค่ารวมภาษี
                    </TableCell>
                    <TableCell align='right' className='nowrap'>
                      ค่าจัดส่ง
                    </TableCell>
                    <TableCell align='right' className='nowrap highlight-success'>
                      รวมทั้งสิ้น
                    </TableCell>
                    <TableCell align='right' className='nowrap'>
                      มัดจำ
                    </TableCell>
                    <TableCell align='right' className='nowrap'>
                      คงเหลือ
                    </TableCell>
                    <TableCell align='center' className='nowrap highlight-warning'>
                      แก้ไขข้อมูล
                    </TableCell>
                    <TableCell align='center' className='nowrap'>
                      ตรวจสอบราคา
                    </TableCell>
                    <TableCell align='center' className='nowrap'>
                      ขออนุมัติ
                    </TableCell>
                    <TableCell align='center' className='nowrap'>
                      อนุมัติ
                    </TableCell>
                    <TableCell align='center' className='nowrap'>
                      ไม่อนุมัติ
                    </TableCell>
                    <TableCell align='center' className='nowrap'>
                      สั่งซื้อ
                    </TableCell>
                    <TableCell align='center' className='nowrap'>
                      รับบางส่วน
                    </TableCell>
                    <TableCell align='center' className='nowrap'>
                      เสร็จสิ้น
                    </TableCell>
                    <TableCell align='center' className='nowrap'>
                      ยกเลิก
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell align='center'>
                        <MDBox className='nowrap'>{row.code}</MDBox>
                        <MDBox className='nowrap'>{Func.statusFaDisplay(row)}</MDBox>
                      </TableCell>
                      <TableCell>
                        <MDBox>{row.supplier_code}</MDBox>
                        <MDBox className='nowrap'>{row.supplier_name}</MDBox>
                      </TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.branch_name}
                      </TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.remark}
                      </TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.type_name}
                      </TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.vat_name}
                      </TableCell>
                      <TableCell align='center'>{Func.dateDisplay(row.date_record)}</TableCell>
                      <TableCell align='center'>
                        <MDBox>{Func.dateDisplay(row.date_due)}</MDBox>
                        {diffDisplay(row)}
                      </TableCell>
                      <TableCell align='right' className='highlight-primary'>
                        {Func.numberDisplay(row.total, 2)}
                      </TableCell>
                      <TableCell align='right'>
                        {Func.numberDisplay(row.discount_value, 2)}
                      </TableCell>
                      <TableCell align='right'>
                        {Func.numberDisplay(row.discount_percent, 2)}%
                      </TableCell>
                      <TableCell align='right' className='highlight-danger'>
                        {Func.numberDisplay(row.discount, 2)}
                      </TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.amount, 2)}</TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.vat_value)}%</TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.vat, 2)}</TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.amount_vat, 2)}</TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.shipping, 2)}</TableCell>
                      <TableCell align='right' className='highlight-success'>
                        {Func.numberDisplay(row.nettotal, 2)}
                      </TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.deposit, 2)}</TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.balance, 2)}</TableCell>
                      <TableCell align='center' className='highlight-warning'>
                        <MDBox>{row.user_edit}</MDBox>
                        <MDBox className='nowrap'>{Func.dateTimeDisplay(row.edit_date)}</MDBox>
                      </TableCell>
                      <TableCell align='center'>
                        <MDBox>{row.user_waiting}</MDBox>
                        <MDBox className='nowrap'>{Func.dateTimeDisplay(row.waiting_date)}</MDBox>
                      </TableCell>
                      <TableCell align='center'>
                        <MDBox>{row.user_request}</MDBox>
                        <MDBox className='nowrap'>{Func.dateTimeDisplay(row.request_date)}</MDBox>
                      </TableCell>
                      <TableCell align='center'>
                        <MDBox>{row.user_approve}</MDBox>
                        <MDBox className='nowrap'>{Func.dateTimeDisplay(row.approve_date)}</MDBox>
                      </TableCell>
                      <TableCell align='center'>
                        <MDBox>{row.user_reject}</MDBox>
                        <MDBox className='nowrap'>{Func.dateTimeDisplay(row.reject_date)}</MDBox>
                      </TableCell>
                      <TableCell align='center'>
                        <MDBox>{row.user_purchase}</MDBox>
                        <MDBox className='nowrap'>{Func.dateTimeDisplay(row.purchase_date)}</MDBox>
                      </TableCell>
                      <TableCell align='center'>
                        <MDBox>{row.user_receive}</MDBox>
                        <MDBox className='nowrap'>{Func.dateTimeDisplay(row.receive_date)}</MDBox>
                      </TableCell>
                      <TableCell align='center'>
                        <MDBox>{row.user_success}</MDBox>
                        <MDBox className='nowrap'>{Func.dateTimeDisplay(row.success_date)}</MDBox>
                      </TableCell>
                      <TableCell align='center'>
                        <MDBox>{row.user_cancel}</MDBox>
                        <MDBox className='nowrap'>{Func.dateTimeDisplay(row.cancel_date)}</MDBox>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell align='right' colSpan={8}>
                      รวม
                    </TableCell>
                    <TableCell align='right' className='highlight-primary'>
                      {Func.numberDisplay(summary.total)}
                    </TableCell>
                    <TableCell colSpan={2} />
                    <TableCell align='right' className='highlight-danger'>
                      {Func.numberDisplay(summary.discount)}
                    </TableCell>
                    <TableCell align='right'>{Func.numberDisplay(summary.amount)}</TableCell>
                    <TableCell />
                    <TableCell align='right'>{Func.numberDisplay(summary.vat)}</TableCell>
                    <TableCell align='right'>{Func.numberDisplay(summary.amount_vat)}</TableCell>
                    <TableCell align='right'>{Func.numberDisplay(summary.shipping)}</TableCell>
                    <TableCell align='right' className='highlight-success'>
                      {Func.numberDisplay(summary.nettotal)}
                    </TableCell>
                    <TableCell align='right'>{Func.numberDisplay(summary.deposit)}</TableCell>
                    <TableCell align='right'>{Func.numberDisplay(summary.balance)}</TableCell>
                    <TableCell colSpan={9} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_purchase`), t(`menu.rp_purchase_data`)]} />
      {renderList()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpPurchaseData;
